/**
 * @file Filter component - Recursive filter display and editing
 * @version 1.1.0
 */

const { computed, watch } = Vue;

// Generate unique IDs for filters
let filterId = 0;
function generateFilterId() {
  return `filter_${filterId++}`;
}

export default {
  name: 'Filter',

  props: {
    filter: {
      type: Object,
      required: true,
    },
    schema: {
      type: Object,
      required: true,
    },
  },

  setup(props, { emit }) {
    const isLogical = computed(() => props.filter.type === 'logical');

    const isComplete = computed(() => {
      if (isLogical.value) return true;
      return props.filter.parameter !== null && props.filter.value !== null;
    });

    const fieldSchema = computed(() => {
      if (!props.filter.parameter) return null;
      return props.schema.properties?.[props.filter.parameter];
    });

    const fieldType = computed(() => {
      if (!fieldSchema.value) return 'string';
      if (fieldSchema.value.enum) return 'enum';
      if (fieldSchema.value.type === 'boolean') return 'boolean';
      if (
        fieldSchema.value.type === 'number' ||
        fieldSchema.value.type === 'integer'
      )
        return 'number';
      if (fieldSchema.value.format === 'date-time') return 'datetime';
      if (fieldSchema.value.format === 'date') return 'date';
      return 'string';
    });

    const availableOperators = computed(() => {
      switch (fieldType.value) {
        case 'number':
          return [
            { value: '$eq', label: '=' },
            { value: '$ne', label: '≠' },
            { value: '$gt', label: '>' },
            { value: '$gte', label: '≥' },
            { value: '$lt', label: '<' },
            { value: '$lte', label: '≤' },
          ];
        case 'boolean':
          return [{ value: '$eq', label: '=' }];
        case 'enum':
          return [
            { value: '$in', label: 'in' },
            { value: '$nin', label: 'not in' },
          ];
        case 'date':
        case 'datetime':
          return [
            { value: '$eq', label: '=' },
            { value: '$gte', label: '≥' },
            { value: '$lte', label: '≤' },
          ];
        default:
          // String: contains (regex) is default
          return [
            { value: '$regex', label: 'contains' },
            { value: '$eq', label: '=' },
            { value: '$ne', label: '≠' },
          ];
      }
    });

    const addFilter = () => {
      props.filter.value.push({
        _id: generateFilterId(),
        type: 'field',
        operator: '$eq',
        parameter: null,
        value: '',
        isNew: true,
      });
    };

    const removeFilter = index => {
      props.filter.value.splice(index, 1);
    };

    const switchToLogical = newOp => {
      props.filter.type = 'logical';
      props.filter.operator = newOp;
      props.filter.value = [];
      delete props.filter.parameter;
    };

    const switchToField = () => {
      props.filter.type = 'field';
      props.filter.operator = '$eq';
      props.filter.parameter = null;
      props.filter.value = '';
    };

    const toggleEnumValue = val => {
      if (!Array.isArray(props.filter.value)) {
        props.filter.value = [];
      }
      const idx = props.filter.value.indexOf(val);
      if (idx >= 0) {
        props.filter.value.splice(idx, 1);
      } else {
        props.filter.value.push(val);
      }
    };

    const selectAllEnum = () => {
      props.filter.value = [...fieldSchema.value.enum];
    };

    const selectNoneEnum = () => {
      props.filter.value = [];
    };

    const logicalOperatorLabel = computed(() => {
      const labels = { $and: 'AND', $or: 'OR', $not: 'NOT' };
      return labels[props.filter.operator] || props.filter.operator;
    });

    const displayOperator = computed(() => {
      const op = availableOperators.value.find(
        o => o.value === props.filter.operator
      );
      return op?.label || props.filter.operator;
    });

    const displayValue = computed(() => {
      if (fieldType.value === 'enum' && Array.isArray(props.filter.value)) {
        if (props.filter.value.length === 0) return 'none';
        if (props.filter.value.length === fieldSchema.value?.enum?.length)
          return 'all';
        if (props.filter.value.length <= 2)
          return props.filter.value.join(', ');
        return `${props.filter.value.length} selected`;
      }
      if (fieldType.value === 'boolean') {
        return ''; // Handled by icon display
      }
      if (Array.isArray(props.filter.value) && !isLogical.value) {
        return props.filter.value.join(', ');
      }
      return props.filter.value;
    });

    const fieldTitle = computed(() => {
      if (!props.filter.parameter) return '';
      return (
        props.schema.properties?.[props.filter.parameter]?.title ||
        props.filter.parameter
      );
    });

    // Boolean display helpers (matching FilterHeader style)
    const booleanIcon = computed(() => {
      if (fieldType.value !== 'boolean') return '';
      if (props.filter.value === true)
        return 'bi-check-circle-fill text-success';
      if (props.filter.value === false) return 'bi-x-circle-fill text-danger';
      return 'bi-circle text-muted';
    });

    const buttonText = computed(() => {
      if (!isComplete.value) return '...';
      if (isLogical.value) return logicalOperatorLabel.value;
      if (fieldType.value === 'boolean') {
        return fieldTitle.value; // Icon will be added separately
      }
      return `${fieldTitle.value} ${displayOperator.value} ${displayValue.value}`;
    });

    watch(
      () => props.filter.parameter,
      (newField, oldField) => {
        if (newField !== oldField && !isLogical.value && newField !== null) {
          const validOps = availableOperators.value.map(o => o.value);
          if (!validOps.includes(props.filter.operator)) {
            props.filter.operator = availableOperators.value[0].value;
          }
          // Reset value based on field type
          if (fieldType.value === 'enum') {
            props.filter.value = [];
          } else if (fieldType.value === 'boolean') {
            props.filter.value = true;
          } else if (fieldType.value === 'string') {
            props.filter.operator = '$regex'; // Default to contains for strings
            props.filter.value = '';
          } else {
            props.filter.value = '';
          }
        }
      }
    );

    return {
      isLogical,
      isComplete,
      fieldSchema,
      fieldType,
      availableOperators,
      addFilter,
      removeFilter,
      switchToLogical,
      switchToField,
      toggleEnumValue,
      selectAllEnum,
      selectNoneEnum,
      logicalOperatorLabel,
      displayOperator,
      displayValue,
      fieldTitle,
      booleanIcon,
      buttonText,
    };
  },

  mounted() {
    if (this.filter.isNew && this.$refs.dropdownButton) {
      delete this.filter.isNew;
      setTimeout(() => {
        if (this.$refs.dropdownButton) {
          const dropdown = bootstrap.Dropdown.getOrCreateInstance(
            this.$refs.dropdownButton
          );
          dropdown.show();
        }
      }, 0);
    }
    if (!this.filter.root && this.$refs.dropdownButton) {
      this.$refs.dropdownButton.addEventListener('hide.bs.dropdown', () => {
        if (!this.isComplete) {
          this.$emit('remove');
        }
      });
    }
  },

  emits: ['remove'],

  template: `
    <div class="d-inline-flex align-items-center gap-1">
      <div v-if="!filter.root" class="dropdown d-inline-block">
        <button
          ref="dropdownButton"
          class="btn btn-sm btn-secondary"
          type="button"
          data-bs-toggle="dropdown"
          data-bs-auto-close="outside"
        >
          <template v-if="fieldType === 'boolean' && isComplete && !isLogical">
            {{ buttonText }}
            <i :class="'ms-1 ' + booleanIcon"></i>
          </template>
          <template v-else>
            {{ buttonText }}
          </template>
        </button>
        <div class="dropdown-menu p-2" style="min-width: 250px;">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div class="btn-group btn-group-sm">
              <button
                class="btn"
                :class="isLogical ? 'btn-outline-secondary active' : 'btn-outline-secondary'"
                @click="switchToLogical('$and')"
                type="button"
              >
                Logical
              </button>
              <button
                class="btn"
                :class="!isLogical ? 'btn-outline-primary active' : 'btn-outline-primary'"
                @click="switchToField"
                type="button"
              >
                Field
              </button>
            </div>
            <button
              class="btn btn-sm btn-outline-danger"
              @click="$emit('remove')"
              type="button"
            >
              <i class="bi-trash"></i>
            </button>
          </div>

          <template v-if="isLogical">
            <label class="form-label small mb-1">Operator</label>
            <div class="btn-group w-100 mb-2">
              <button
                class="btn btn-sm"
                :class="filter.operator === '$and' ? 'btn-secondary' : 'btn-outline-secondary'"
                @click="filter.operator = '$and'"
                type="button"
              >
                AND
              </button>
              <button
                class="btn btn-sm"
                :class="filter.operator === '$or' ? 'btn-secondary' : 'btn-outline-secondary'"
                @click="filter.operator = '$or'"
                type="button"
              >
                OR
              </button>
              <button
                class="btn btn-sm"
                :class="filter.operator === '$not' ? 'btn-secondary' : 'btn-outline-secondary'"
                @click="filter.operator = '$not'"
                type="button"
              >
                NOT
              </button>
            </div>
          </template>

          <template v-else>
            <label class="form-label small mb-1">Field</label>
            <select v-model="filter.parameter" class="form-select form-select-sm mb-2">
              <option :value="null">Select field...</option>
              <option
                v-for="(prop, key) in schema.properties"
                :key="key"
                :value="key"
              >
                {{ prop.title || key }}
              </option>
            </select>

            <template v-if="isComplete">
              <label class="form-label small mb-1" v-if="availableOperators.length > 1">Operator</label>
              <div v-if="availableOperators.length > 1" class="btn-group w-100 mb-2">
                <button
                  v-for="op in availableOperators"
                  :key="op.value"
                  class="btn btn-sm"
                  :class="filter.operator === op.value ? 'btn-primary' : 'btn-outline-primary'"
                  @click="filter.operator = op.value"
                  type="button"
                >
                  {{ op.label }}
                </button>
              </div>

              <label class="form-label small mb-1">Value</label>

              <!-- Enum: checkbox list -->
              <div v-if="fieldType === 'enum'">
                <div class="mb-2">
                  <button
                    @click="selectAllEnum"
                    class="btn btn-sm btn-outline-primary me-1"
                    type="button"
                  >
                    All
                  </button>
                  <button
                    @click="selectNoneEnum"
                    class="btn btn-sm btn-outline-secondary"
                    type="button"
                  >
                    None
                  </button>
                </div>
                <div class="form-check" v-for="val in fieldSchema.enum" :key="val">
                  <input
                    :id="'enum-' + filter.parameter + '-' + val"
                    class="form-check-input"
                    type="checkbox"
                    :checked="Array.isArray(filter.value) && filter.value.includes(val)"
                    @change="toggleEnumValue(val)"
                  />
                  <label
                    class="form-check-label"
                    :for="'enum-' + filter.parameter + '-' + val"
                  >
                    {{ val }}
                  </label>
                </div>
              </div>

              <!-- Boolean: button group with icons -->
              <div v-else-if="fieldType === 'boolean'" class="btn-group w-100">
                <button
                  class="btn btn-sm"
                  :class="filter.value === true ? 'btn-success' : 'btn-outline-secondary'"
                  @click="filter.value = true"
                  type="button"
                >
                  <i class="bi bi-check-circle-fill"></i> true
                </button>
                <button
                  class="btn btn-sm"
                  :class="filter.value === false ? 'btn-danger' : 'btn-outline-secondary'"
                  @click="filter.value = false"
                  type="button"
                >
                  <i class="bi bi-x-circle-fill"></i> false
                </button>
              </div>

              <!-- Date: date picker -->
              <div v-else-if="fieldType === 'date'">
                <input
                  v-model="filter.value"
                  class="form-control form-control-sm"
                  type="date"
                />
              </div>

              <!-- DateTime: datetime-local picker -->
              <div v-else-if="fieldType === 'datetime'">
                <input
                  v-model="filter.value"
                  class="form-control form-control-sm"
                  type="datetime-local"
                />
              </div>

              <!-- Number -->
              <div v-else-if="fieldType === 'number'">
                <input
                  v-model="filter.value"
                  class="form-control form-control-sm"
                  type="number"
                />
              </div>

              <!-- String (default) -->
              <div v-else>
                <input
                  v-model="filter.value"
                  class="form-control form-control-sm"
                  type="text"
                  placeholder="Search..."
                />
              </div>
            </template>
          </template>
        </div>
      </div>

      <template v-if="isLogical">
        <span v-if="!filter.root">[</span>
        <template v-for="(child, idx) in filter.value" :key="child._id">
          <Filter :filter="child" :schema="schema" @remove="removeFilter(idx)" />
        </template>
        <button
          @click="addFilter"
          class="btn btn-sm btn-outline-secondary"
          type="button"
        >
          + <i class="bi bi-funnel"></i>
        </button>
        <span v-if="!filter.root">]</span>
      </template>
    </div>
  `,
};
